import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  ButtonGroup,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardText,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import moment from "moment";
import { TableHeader, Pagination, Search } from "../../DataTable";
import useFullPageLoader from "../../hooks/useFullPageLoader";
import SelectorGrupos from "../../common/SelectorGrupos";

const money = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v || 0);

// Rangos de % de utilidad (fraccion 0-1) para los indicadores clicables.
const RANGOS = [
  { key: "neg", label: "< 0%", color: "#c62828", test: (v) => v < 0 },
  { key: "0-5", label: "0% a 5%", color: "#c62828", test: (v) => v >= 0 && v < 0.05 },
  { key: "5-15", label: "5% a 15%", color: "#f9a825", test: (v) => v >= 0.05 && v < 0.15 },
  { key: "15-30", label: "15% a 30%", color: "#2e7d32", test: (v) => v >= 0.15 && v < 0.3 },
  { key: ">30", label: "> 30%", color: "#2e7d32", test: (v) => v >= 0.3 },
];

function ReporteUtilidad() {
  const { user } = useContext(AuthContext);
  const URL_DASHBOARDS = process.env.REACT_APP_URL_DASHBOARDS;
  const URL_GET_MEDIA = process.env.REACT_APP_URL_GETMEDIA;
  const URL_FOTO_CLOUDINARY = process.env.REACT_APP_URL_FOTO_CLOUDINARY;
  const puedeVerCostos = user.inventarios_costos;

  const [productos, setProductos] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const ITEMS_PER_PAGE = 50;

  const [selectedCategorias, setSelectedCategorias] = useState([]); // multi-categoria
  const [selectedRango, setSelectedRango] = useState("");

  // Rango de fechas del reporte (utilidad de las ventas del periodo)
  const [fechaInicio, setFechaInicio] = useState(moment().startOf("month").format("YYYY-MM-DD"));
  const [fechaFin, setFechaFin] = useState(moment().format("YYYY-MM-DD"));

  // Grupos de clientes elegidos antes de generar el reporte
  const [gruposSel, setGruposSel] = useState([]);
  const [gruposInfo, setGruposInfo] = useState([]);
  const [modalGrupos, setModalGrupos] = useState(true);

  // Imagen del producto
  const [modalFoto, setModalFoto] = useState(false);
  const toggleFoto = () => setModalFoto(!modalFoto);
  const [photo, setPhoto] = useState("");
  const [idFoto, setIdFoto] = useState("");

  const toggleCategoria = (id) => {
    setSelectedCategorias((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    setCurrentPage(1);
  };

  const confirmarGrupos = (ids, grupos) => {
    setGruposSel(ids);
    setGruposInfo(grupos);
    setModalGrupos(false);
  };

  // Carga de utilidad real por grupo (una fila por articulo x grupo)
  useEffect(() => {
    if (!gruposSel.length) {
      setProductos([]);
      return;
    }
    showLoader();
    const headers = { Authorization: `Bearer: ${localStorage.getItem("app_token")}` };
    const q = `?grupos=${gruposSel.join(",")}`;
    axios
      .get(`${URL_DASHBOARDS}UtilidadGrupo/${fechaInicio}/${fechaFin}${q}`, { headers })
      .then((res) => {
        // PctUtilidad (fraccion) para los indicadores de rango
        const data = (res.data || []).map((r) => ({
          ...r,
          PctUtilidad: (Number(r.Margen) || 0) / 100,
        }));
        setProductos(data);
        setSelectedRango("");
        setCurrentPage(1);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gruposSel, fechaInicio, fechaFin]);

  // Categorias disponibles
  const categorias = useMemo(() => {
    const map = {};
    productos.forEach((p) => {
      if (!map[p.idCategoria]) map[p.idCategoria] = p.Categoria;
    });
    return Object.entries(map)
      .map(([idCategoria, Categoria]) => ({ idCategoria, Categoria }))
      .sort((a, b) => (a.Categoria > b.Categoria ? 1 : -1));
  }, [productos]);

  function verFoto(idArticulo) {
    setPhoto("");
    setIdFoto(idArticulo);
    axios
      .get(`${URL_GET_MEDIA}/${idArticulo}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((data) => {
        if (data.data.version) setPhoto(data.data.version);
      })
      .catch((err) => console.log(err));
    toggleFoto();
  }

  // Lista base (categoria + busqueda), sin filtro de rango
  const baseList = useMemo(() => {
    let lista = [...productos];
    if (selectedCategorias.length > 0)
      lista = lista.filter((e) => selectedCategorias.includes(String(e.idCategoria)));
    if (search) {
      const s = search.toLowerCase();
      lista = lista.filter(
        (e) =>
          (e.Codigo || "").toLowerCase().includes(s) ||
          (e.Nombre || "").toLowerCase().includes(s) ||
          (e.Grupo || "").toLowerCase().includes(s)
      );
    }
    return lista;
  }, [productos, selectedCategorias, search]);

  const rangos = useMemo(
    () => RANGOS.map((r) => ({ ...r, count: baseList.filter((p) => r.test(p.PctUtilidad)).length })),
    [baseList]
  );

  const calculados = useMemo(() => {
    if (!selectedRango) return baseList;
    const r = RANGOS.find((x) => x.key === selectedRango);
    return r ? baseList.filter((p) => r.test(p.PctUtilidad)) : baseList;
  }, [baseList, selectedRango]);

  // KPIs
  const kpis = useMemo(() => {
    const venta = calculados.reduce((s, p) => s + (Number(p.Venta) || 0), 0);
    const costo = calculados.reduce((s, p) => s + (Number(p.Costo) || 0), 0);
    const utilidad = venta - costo;
    const margen = venta > 0 ? (utilidad / venta) * 100 : 0;
    return { venta, costo, utilidad, margen };
  }, [calculados]);

  const headers = [
    { name: "Grupo", field: "Grupo", sortable: true },
    { name: "Item", field: "Codigo", sortable: true },
    { name: "Producto", field: "Nombre", sortable: true },
    { name: "Categoria", field: "Categoria", sortable: true },
    { name: "Cantidad", field: "Cantidad", sortable: true },
    { name: "Precio Prom.", field: "PrecioPromedio", sortable: true },
    { name: "Costo Unit.", field: "CostoUnitario", sortable: true },
    { name: "Venta", field: "Venta", sortable: true },
    { name: "Costo", field: "Costo", sortable: true },
    { name: "Utilidad", field: "Utilidad", sortable: true },
    { name: "% Utilidad", field: "Margen", sortable: true },
    { name: "Img", field: "img", sortable: false },
  ];

  const commentsData = useMemo(() => {
    let computed = [...calculados];
    const numFields = ["Cantidad", "PrecioPromedio", "CostoUnitario", "Venta", "Costo", "Utilidad", "Margen"];
    if (sorting.field && !numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = computed.sort((a, b) => reversed * (a[sorting.field] || "").localeCompare(b[sorting.field] || ""));
    }
    if (sorting.field && numFields.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = computed.sort((a, b) => reversed * (parseFloat(a[sorting.field]) - parseFloat(b[sorting.field])));
    }
    setTotalItems(computed.length);
    return computed.slice((currentPage - 1) * ITEMS_PER_PAGE, (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE);
  }, [calculados, currentPage, sorting]);

  function excel() {
    const dataExcel = calculados.map((a) => ({
      Grupo: a.Grupo,
      Item: a.Codigo,
      Producto: a.Nombre,
      Categoria: a.Categoria,
      Cantidad: a.Cantidad,
      PrecioPromedio: (Number(a.PrecioPromedio) || 0).toFixed(2),
      CostoUnitario: (Number(a.CostoUnitario) || 0).toFixed(2),
      Venta: (Number(a.Venta) || 0).toFixed(2),
      Costo: (Number(a.Costo) || 0).toFixed(2),
      Utilidad: (Number(a.Utilidad) || 0).toFixed(2),
      PctUtilidad: (Number(a.Margen) || 0).toFixed(2),
    }));
    const filtro = ["Grupo", "Item", "Producto", "Categoria", "Cantidad", "PrecioPromedio", "CostoUnitario", "Venta", "Costo", "Utilidad", "PctUtilidad"];
    new ExportJsonExcel({
      fileName: "UtilidadPorGrupo",
      datas: [{ sheetData: dataExcel, sheetName: "UtilidadPorGrupo", sheetFilter: filtro, sheetHeader: filtro }],
    }).saveExcel();
  }

  function PDFTabla() {
    const data = calculados.map((a) => [
      a.Grupo,
      a.Codigo,
      a.Nombre,
      new Intl.NumberFormat("en-US").format(a.Cantidad),
      money(a.Venta),
      money(a.Costo),
      money(a.Utilidad),
      `${(Number(a.Margen) || 0).toFixed(1)}%`,
    ]);
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 120, 15, 45, 15);
    doc.text(`Utilidad por Grupo (${fechaInicio} a ${fechaFin})`, 20, 30);
    doc.autoTable({
      head: [["Grupo", "Item", "Producto", "Cantidad", "Venta", "Costo", "Utilidad", "% Utilidad"]],
      body: data,
      startY: 40,
      didParseCell: function (d) {
        if (d.section === "body") {
          const fila = calculados[d.row.index];
          if (fila && fila.PctUtilidad < 0.05) d.cell.styles.textColor = [198, 40, 40];
        }
      },
    });
    doc.save(`UtilidadPorGrupo.pdf`);
  }

  return (
    <>
      <Header />
      <SelectorGrupos
        isOpen={modalGrupos}
        onConfirm={confirmarGrupos}
        onClose={() => setModalGrupos(false)}
        allowClose={gruposSel.length > 0}
        initialSelected={gruposSel}
      />
      <br />
      <br />
      {user.menu_reportes ? (
        !puedeVerCostos ? (
          <div className="card col-12">
            <h4 align="center">No tienes permiso para ver costos y utilidades.</h4>
          </div>
        ) : (
          <div className="card col-12">
            <Row>
              <Col md={8}>
                <Button size="sm" href="/ReportesBI" className="btn btn-danger" id="botonListado">
                  Regresar
                </Button>
                <h3>Utilidad por Grupo</h3>
                <div>
                  <small style={{ color: "#555" }}>
                    Utilidad real de las ventas del periodo. Grupos:{" "}
                    <b>{gruposInfo.map((g) => g.name).join(", ") || "—"}</b>{" "}
                  </small>
                  <Button size="sm" color="link" className="p-0" onClick={() => setModalGrupos(true)}>
                    Cambiar grupos
                  </Button>
                </div>
              </Col>
              <Col sm={4}>
                <ButtonGroup id="logoutBoton">
                  <Button size="sm" className="btn" color="danger" onClick={PDFTabla} disabled={!productos.length}>
                    PDF <i class="far fa-file-pdf"></i>
                  </Button>
                  <Button size="sm" className="btn" color="primary" onClick={excel} disabled={!productos.length}>
                    Excel <i class="far fa-file-excel"></i>
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {/* Rango de fechas */}
            <Row>
              <Col md={3}>
                <Label className="mr-sm-2">Fecha inicio</Label>
                <Input bsSize="sm" type="date" value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)} />
              </Col>
              <Col md={3}>
                <Label className="mr-sm-2">Fecha fin</Label>
                <Input bsSize="sm" type="date" value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)} />
              </Col>
            </Row>
            <br />

            {!gruposSel.length ? (
              <h4 align="center" style={{ color: "#888", padding: "40px 0" }}>
                Selecciona uno o varios grupos para ver la utilidad.
              </h4>
            ) : (
              <>
                {/* Categorias (multi) + busqueda */}
                <Row>
                  <Col md={8}>
                    <span className="negrita" style={{ marginRight: 12 }}>
                      Categorias:{" "}
                      {selectedCategorias.length > 0 && (
                        <small style={{ color: "#1565c0", cursor: "pointer" }} onClick={() => setSelectedCategorias([])}>
                          (limpiar)
                        </small>
                      )}
                    </span>
                    {categorias.map((c) => (
                      <label key={c.idCategoria} style={{ marginRight: 16, fontWeight: "normal", cursor: "pointer", whiteSpace: "nowrap", display: "inline-block" }}>
                        <input type="checkbox" checked={selectedCategorias.includes(String(c.idCategoria))} onChange={() => toggleCategoria(String(c.idCategoria))} style={{ marginRight: 5, verticalAlign: "middle" }} />
                        <span style={{ verticalAlign: "middle" }}>{c.Categoria}</span>
                      </label>
                    ))}
                  </Col>
                  <Col md={4} className="d-flex flex-row-reverse">
                    <Search onSearch={(value) => { setSearch(value); setCurrentPage(1); }} />
                  </Col>
                </Row>
                <br />

                {/* KPIs de totales */}
                <Row style={{ justifyContent: "center" }}>
                  <Col md={3}>
                    <Card className="text-center" style={{ borderTop: "4px solid #1565c0" }}>
                      <CardHeader tag="h6">Venta</CardHeader>
                      <CardBody><CardText tag="h4">{money(kpis.venta)}</CardText></CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center" style={{ borderTop: "4px solid #c62828" }}>
                      <CardHeader tag="h6">Costo</CardHeader>
                      <CardBody><CardText tag="h4">{money(kpis.costo)}</CardText></CardBody>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="text-center" style={{ borderTop: "4px solid #2e7d32" }}>
                      <CardHeader tag="h6">Utilidad</CardHeader>
                      <CardBody>
                        <CardText tag="h4" style={{ color: kpis.utilidad >= 0 ? "#2e7d32" : "#c62828" }}>
                          {money(kpis.utilidad)}
                        </CardText>
                        <small>{kpis.margen.toFixed(1)}% margen</small>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
                <br />

                {/* Indicadores por rango (clickeables) */}
                <Row style={{ justifyContent: "center" }}>
                  {rangos.map((r) => {
                    const activo = selectedRango === r.key;
                    return (
                      <Col md={2} key={r.key}>
                        <Card className="text-center" style={{ borderTop: `4px solid ${r.color}`, cursor: "pointer", boxShadow: activo ? `0 0 0 2px ${r.color}` : "none" }}
                          onClick={() => { setSelectedRango(activo ? "" : r.key); setCurrentPage(1); }}
                          title="Clic para filtrar los productos en este rango">
                          <CardHeader tag="h6" style={{ padding: "6px" }}>{r.label}</CardHeader>
                          <CardBody style={{ padding: "8px" }}>
                            <CardText tag="h4" style={{ color: r.color, fontWeight: "bold", margin: 0 }}>{r.count}</CardText>
                            <small style={{ color: r.color }}>{activo ? "filtrando ✓" : "clic"}</small>
                          </CardBody>
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
                <br />

                <Pagination total={totalItems} itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
                <Table size="sm" striped borderless className="table-responsive-xl">
                  <TableHeader headers={headers} onSorting={(field, order) => setSorting({ field, order })} />
                  <tbody>
                    {commentsData.map((a) => {
                      const rojo = a.PctUtilidad < 0.05;
                      return (
                        <tr key={`${a.idArticulo}-${a.idGrupo}`} style={rojo ? { color: "#c62828" } : {}}>
                          <td>{a.Grupo}</td>
                          <td>{a.Codigo}</td>
                          <td>{a.Nombre}</td>
                          <td>{a.Categoria}</td>
                          <td>{new Intl.NumberFormat("en-US").format(a.Cantidad)}</td>
                          <td>{money(a.PrecioPromedio)}</td>
                          <td>{money(a.CostoUnitario)}</td>
                          <td>{money(a.Venta)}</td>
                          <td>{money(a.Costo)}</td>
                          <td>{money(a.Utilidad)}</td>
                          <td>{(Number(a.Margen) || 0).toFixed(1)}%</td>
                          <td>
                            <Button size="sm" color="info" onClick={() => verFoto(a.idArticulo)} title="Ver imagen">
                              <i class="far fa-image"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <div className="col-md-6">
                  <Pagination total={totalItems} itemsPerPage={ITEMS_PER_PAGE} currentPage={currentPage} onPageChange={(page) => setCurrentPage(page)} />
                </div>
              </>
            )}
          </div>
        )
      ) : undefined}

      <Modal size="sm" isOpen={modalFoto} toggle={toggleFoto}>
        <ModalHeader toggle={toggleFoto}>
          <h4>Imagen del Producto</h4>
        </ModalHeader>
        <ModalBody>
          <Row>
            {photo ? (
              <h6 style={{ textAlign: "center" }}>
                <img src={`${URL_FOTO_CLOUDINARY}/image/upload/v${photo}/shopilincArticulos/${idFoto}.jpg`} alt="" style={{ maxWidth: "100%" }} />
              </h6>
            ) : (
              <h4>No se ha subido una imagen....</h4>
            )}
          </Row>
        </ModalBody>
      </Modal>
      <br />
      {loader}
    </>
  );
}

export default ReporteUtilidad;
